import { useState } from "react";
import { Reveal } from "@/components/Reveal";

const MK = [
  ["Madison", 12.56, 133], ["Spokane TV", 7.28, 23], ["Spokane Radio", 6.89, 137],
  ["Joplin-Pittsburg", 3.77, 8], ["La Crosse", 2.74, 8], ["Victoria", 1.93, 4],
  ["Platteville Radio", 1.59, 30], ["Kennewick/Yakima", 1.42, 25], ["Houghton", 0.5, 3],
  ["Marquette", 0.46, 6], ["Iron River", 0.21, 2], ["Alpena", 0.14, 3],
];
const THIN = ["Joplin-Pittsburg", "La Crosse", "Victoria", "Houghton", "Iron River"];

const FACTS = [
  ["Station-level budgets", "Owner \u00d7 station \u00d7 account code \u00d7 month. 16 owner tabs per market, up to 14 stations each."],
  ["19 account codes", "15 shared by all markets, plus 4 Magazine codes for Madison only. One object handles both templates."],
  ["Templates are empty", "No 2027 figures yet. We'd build and test on the 2026 file in a sandbox meanwhile."],
];

const ISSUES = [
  ["bad", "Canadian Sales is coded 2500 (Paid Programming) on every AE tab. It should be 2305."],
  ["bad", "Station tabs total Paid Programming from the AE 1 row only."],
  ["bad", "The market-level Paids tab has no inputs and isn't linked to the totals."],
  ["bad", "Madison's Magazine rows carry broadcast codes, so they'd map to Canadian Sales."],
  ["warn", "La Crosse's Paids tab lists Spokane stations (KXLX, KXLY, KZZU)."],
  ["warn", "Properties 4-7 all share code 22, so we need the station name as well."],
];

const CHANGE = [
  ["Code mismatch on booked revenue", "Billing records need to map to the same 4-digit account codes as budgets. This wasn't in our first estimate."],
  ["Line items come back", "The original setup removed them. AEs need a product library (station + account code) and training on entering deals."],
  ["Excluded", "Rebuilding 2026 budgets, changes to the WideOrbit integration, and re-imports after budget figures change."],
];

export default function Findings() {
  const [metric, setMetric] = useState("usd");
  const idx = metric === "usd" ? 1 : 2;
  const max = Math.max(...MK.map((r) => r[idx]));

  return (
    <section id="found" className="mx-auto max-w-6xl px-4 sm:px-6 mt-14 scroll-mt-16" data-testid="section-found">
      <Reveal>
        <p className="font-mono text-[11.5px] uppercase tracking-[0.08em] text-fog">What we found</p>
        <h2 className="mt-2 font-display text-2xl sm:text-3xl text-coal">
          The documents support the approach, with a few things to fix
        </h2>
        <p className="mt-2 text-[15px] text-fog max-w-3xl">
          Budgets are set at a finer level than we first assumed, both templates have
          errors that would break an import, and a few requirements go beyond what the
          change order describes.
        </p>
      </Reveal>

      <div className="mt-5 grid md:grid-cols-3 gap-3" data-testid="found-facts">
        {FACTS.map(([t, b]) => (
          <div key={t} className="border-t-2 border-coal pt-2.5">
            <p className="font-display text-[15px] text-coal">{t}</p>
            <p className="mt-1 text-[14px] text-fog leading-relaxed">{b}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 grid lg:grid-cols-2 gap-4 items-start">
        <Reveal>
          <div className="bg-white border border-hair rounded-md p-5" data-testid="found-issues">
            <h3 className="font-display text-lg text-coal">Issues to fix in the templates before import</h3>
            <ul className="mt-4 grid gap-2">
              {ISSUES.map(([sev, text], i) => (
                <li key={i} className="flex gap-2.5 items-start text-[14px] text-coal/85 leading-relaxed">
                  <span className={`mt-[7px] w-2 h-2 rounded-full shrink-0 ${sev === "bad" ? "bg-bad" : "bg-warn"}`} data-testid={`found-issue-severity-${i + 1}`} />
                  {text}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[12.5px] text-fog leading-relaxed">
              We'll send back a cleaned-up template, import from the owner and Paids tabs,
              and give each market an exceptions report wherever the numbers don't tie out.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="bg-white border border-hair rounded-md p-5" data-testid="found-market-chart">
            <div className="flex flex-wrap justify-between items-center gap-2.5">
              <h3 className="font-display text-lg text-coal">Current budget (Jun-Dec 2026) by market</h3>
              <div className="inline-flex border border-hair rounded overflow-hidden bg-white" role="group" aria-label="Measure" data-testid="found-chart-toggle">
                {[
                  ["usd", "Budget"],
                  ["rows", "Budget lines"],
                ].map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`found-chart-${k}`}
                    onClick={() => setMetric(k)}
                    className={`px-3 py-1.5 font-mono text-[11.5px] transition-colors duration-200 ${metric === k ? "bg-coal text-white" : "text-fog hover:text-coal"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4 grid gap-1.5">
              {MK.map((r) => {
                const thin = metric === "rows" && THIN.includes(r[0]);
                return (
                  <div key={r[0]} className="grid grid-cols-[110px_1fr_64px] md:grid-cols-[150px_1fr_72px] gap-2.5 items-center text-[13px]" data-testid={`found-market-bar-${r[0].replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    <span className="text-coal/85 truncate">{r[0]}</span>
                    <span className="h-3.5 bg-sunk rounded-[2px] overflow-hidden block">
                      <span
                        className={`block h-full rounded-[2px] transition-[width] duration-300 ${thin ? "bg-warn/70" : "bg-coal"}`}
                        style={{ width: `${(r[idx] / max) * 100}%` }}
                      />
                    </span>
                    <span className="font-mono text-right text-coal/80">
                      {metric === "usd" ? `$${r[1].toFixed(2)}M` : r[2]}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="mt-4 text-[12.5px] text-fog leading-relaxed">
              {metric === "usd"
                ? "$39.5M across 12 markets and 59 owners. This is the budget behind today's 382 Goals."
                : "Amber marks markets budgeting at one or two lines per AE today. For them, the station \u00d7 account code template is a big jump."}
            </p>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.08}>
        <div className="mt-4 bg-white border border-hair rounded-md p-5" data-testid="found-change-rows">
          <h3 className="font-display text-lg text-coal">Also from the change order</h3>
          <table className="mt-3 w-full text-[14px]">
            <tbody>
              {CHANGE.map(([t, b]) => (
                <tr key={t} className="border-b border-hair last:border-0 align-top">
                  <td className="py-3 pr-4 font-medium text-coal lg:w-[34%]">{t}</td>
                  <td className="py-3 text-fog leading-relaxed">{b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Reveal>
    </section>
  );
}
